import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChangeCircleOutlinedIcon from '@mui/icons-material/ChangeCircleOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import RuleIcon from '@mui/icons-material/Rule';
import { Link as RouterLink, useNavigation, useParams, useSearchParams } from 'react-router-dom';
import { ApiError } from '../../lib/api';
import { useDocumentTitle } from '../../hooks';
import { EmptyState, PageLoadingSpinner } from '../../components/ui';
import { useToast } from '../../components/Toast';
import { enableGraphMonitoring, fetchGraphMonitoringDetail } from './monitoringApi';
import { GraphMonitoringEntityDetail } from './types';
import {
  buildDiffDisplayModel,
  buildSnapshotNodeLookup,
  formatList,
} from './detailDisplayUtils';
import {
  formatDate,
  formatRelativeTime,
  getDetailDisplayName,
} from './utils';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/i18n';

function getErrorState(error: unknown): { status: number | null; message: string } {
  if (error instanceof ApiError) {
    if (error.status === 400) {
      return {
        status: error.status,
        message: i18n.t('monitoring.detail.invalidOrgNumber'),
      };
    }

    if (typeof error.body === 'string' && error.body.trim()) {
      return {
        status: error.status,
        message: error.body,
      };
    }

    return {
      status: error.status,
      message: error.message,
    };
  }

  if (error instanceof Error) {
    return { status: null, message: error.message };
  }

  return {
    status: null,
    message: i18n.t('monitoring.detailLoadFailed'),
  };
}

export default function GraphMonitoringDetail() {
  const { t } = useTranslation();
  const { orgNumber = '' } = useParams();
  const [searchParams] = useSearchParams();
  const navigation = useNavigation();
  const toast = useToast();
  const [detail, setDetail] = useState<GraphMonitoringEntityDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusCode, setStatusCode] = useState<number | null>(null);
  const [notMonitored, setNotMonitored] = useState(false);
  const presetDisplayName = searchParams.get('displayName')?.trim() ?? '';

  const pageHeading = detail
    ? getDetailDisplayName(detail, presetDisplayName)
    : presetDisplayName || orgNumber;
  const title = `${pageHeading || t('monitoring.overview.company')} — ${t('monitoring.overviewTitle')} — Breadify`;
  useDocumentTitle(title);

  const applyDetail = useCallback(
    (nextDetail: GraphMonitoringEntityDetail) => {
      setDetail(nextDetail);
      setNotMonitored(false);
      setStatusCode(null);
    },
    [],
  );

  const fetchPage = useCallback(
    async (mode: 'initial' | 'refresh' = 'initial') => {
      if (!orgNumber) {
        setStatusCode(400);
        setError(t('monitoring.detail.orgNumberRequired'));
        setLoading(false);
        return;
      }

      if (mode === 'initial') {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      setError(null);
      setStatusCode(null);

      try {
        const detailResult = await fetchGraphMonitoringDetail(orgNumber, {
          snapshotLimit: 20,
          diffLimit: 20,
          preferredDisplayName: presetDisplayName,
        });

        applyDetail(detailResult);
      } catch (fetchError) {
        if (fetchError instanceof ApiError && fetchError.status === 404) {
          setDetail(null);
          setNotMonitored(true);
          setStatusCode(null);
        } else {
          const nextErrorState = getErrorState(fetchError);
          setStatusCode(nextErrorState.status);
          setError(nextErrorState.message);
          setDetail(null);
          setNotMonitored(false);
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [applyDetail, orgNumber, presetDisplayName, t],
  );

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const monitoringEnabled = detail?.monitoredEntity.enabled ?? false;
  const assessmentPath = `/companyassessment/${orgNumber}`;
  const openingAssessment =
    navigation.state === 'loading' && navigation.location?.pathname === assessmentPath;
  const snapshotNodeLookup = useMemo(
    () => buildSnapshotNodeLookup(detail?.snapshots ?? []),
    [detail],
  );
  const ruleLookup = useMemo(
    () =>
      new Map(
        (detail?.ruleSubscriptions ?? []).map((rule) => [rule.ruleKey, rule.displayName]),
      ),
    [detail],
  );
  const sortedDiffs = useMemo(
    () =>
      (detail?.diffs ?? [])
        .slice()
        .sort((left, right) => right.detectedAt.localeCompare(left.detectedAt, 'sv')),
    [detail],
  );
  const notificationDiffs = useMemo(
    () => sortedDiffs.filter((diff) => diff.ruleMatches.length > 0 || diff.shouldNotify),
    [sortedDiffs],
  );
  const displayedDiffs = notificationDiffs.length > 0 ? notificationDiffs : sortedDiffs;

  const handleStartMonitoring = useCallback(async () => {
    if (!orgNumber) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const savedDetail = await enableGraphMonitoring(
        orgNumber,
        presetDisplayName || orgNumber,
      );

      applyDetail(savedDetail);
      toast.success(t('monitoring.detail.enabled'));
    } catch (saveError) {
      const nextError = getErrorState(saveError);
      setStatusCode(nextError.status);
      setError(nextError.message);
      toast.error(nextError.message);
    } finally {
      setSaving(false);
    }
  }, [applyDetail, orgNumber, presetDisplayName, t, toast]);

  if (loading) {
    return (
      <Box sx={{ width: '100%', minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <PageLoadingSpinner label={t('monitoring.detail.loading')} />
      </Box>
    );
  }

  if (!detail && !notMonitored) {
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button
          component={RouterLink}
          to="/graphmonitoring"
          variant="text"
          startIcon={<ArrowBackIcon />}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('monitoring.detail.backToMonitoring')}
        </Button>
        <Paper
          sx={{
            borderRadius: 'var(--radius-xl)',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <EmptyState
            icon={<ChangeCircleOutlinedIcon />}
            title={t(statusCode === 404 ? 'monitoring.companyNotFound' : 'monitoring.detailLoadFailed')}
            description={error ?? t('monitoring.noDetail')}
            action={
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={() => fetchPage('refresh')}
              >
                {t('common.actions.tryAgain')}
              </Button>
            }
          />
        </Paper>
      </Box>
    );
  }

  if (notMonitored) {
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Breadcrumbs aria-label={t('monitoring.detail.breadcrumb')}>
          <Link component={RouterLink} underline="hover" color="inherit" to="/graphmonitoring">
            {t('monitoring.overviewTitle')}
          </Link>
          <Typography color="text.primary">{pageHeading}</Typography>
        </Breadcrumbs>

        <Paper
          sx={{
            p: 3,
            borderRadius: 'var(--radius-xl)',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
              {pageHeading}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720 }}>
              {t('monitoring.detail.notMonitoredHelp')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={t('monitoring.notMonitored')} size="small" />
            <Chip label={t('monitoring.detail.orgNumber', { value: orgNumber })} size="small" variant="outlined" />
          </Box>

          {error && <Alert severity="warning">{error}</Alert>}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <RuleIcon />}
              onClick={handleStartMonitoring}
              disabled={saving}
            >
              {saving ? t('monitoring.detail.adding') : t('monitoring.detail.addToMonitoring')}
            </Button>
            <Button
              component={RouterLink}
              to={assessmentPath}
              variant="outlined"
              startIcon={
                openingAssessment
                  ? <CircularProgress size={18} color="inherit" />
                  : <OpenInNewIcon />
              }
              disabled={openingAssessment}
            >
              {openingAssessment ? t('monitoring.detail.opening') : t('monitoring.detail.openAssessment')}
            </Button>
            <Button
              component={RouterLink}
              to="/graphmonitoring"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
            >
              {t('common.actions.back')}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Breadcrumbs aria-label={t('monitoring.detail.breadcrumb')}>
        <Link component={RouterLink} underline="hover" color="inherit" to="/graphmonitoring">
          {t('monitoring.overviewTitle')}
        </Link>
        <Typography color="text.primary">{pageHeading}</Typography>
      </Breadcrumbs>

      <Paper
        sx={{
          p: 3,
          borderRadius: 'var(--radius-xl)',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: { xs: 'flex-start', md: 'center' },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
              {pageHeading}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760 }}>
              {t('monitoring.detail.recentNotifications')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={t(monitoringEnabled ? 'monitoring.detail.on' : 'monitoring.detail.off')}
                color={monitoringEnabled ? 'success' : 'default'}
                size="small"
              />
              <Chip
                label={t('monitoring.detail.orgNumber', { value: detail?.monitoredEntity.externalId ?? orgNumber })}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              component={RouterLink}
              to={assessmentPath}
              variant="outlined"
              startIcon={
                openingAssessment
                  ? <CircularProgress size={18} color="inherit" />
                  : <OpenInNewIcon />
              }
              disabled={openingAssessment}
            >
              {openingAssessment ? t('monitoring.detail.opening') : t('monitoring.detail.openAssessment')}
            </Button>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              disabled={refreshing}
              onClick={() => fetchPage('refresh')}
            >
              {refreshing ? t('monitoring.overview.refreshing') : t('common.actions.refresh')}
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && <Alert severity="warning">{error}</Alert>}

      {displayedDiffs.length === 0 ? (
        <Paper
          sx={{
            borderRadius: 'var(--radius-xl)',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <EmptyState
            icon={<ChangeCircleOutlinedIcon />}
            title={t(monitoringEnabled ? 'monitoring.detail.noNotifications' : 'monitoring.detail.noStoredNotifications')}
            description={
              monitoringEnabled
                ? t('monitoring.detail.noNotificationsHelp')
                : t('monitoring.detail.noStoredNotificationsHelp')
            }
            sx={{ py: 6 }}
          />
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {displayedDiffs.map((diff) => {
            const displayModel = buildDiffDisplayModel(
              diff,
              snapshotNodeLookup,
              detail?.monitoredEntity.externalId ?? orgNumber,
              ruleLookup,
            );

            return (
              <Paper
                key={diff.id}
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: 'var(--radius-xl)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {formatDate(diff.detectedAt)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatRelativeTime(diff.detectedAt) ?? t('monitoring.notificationReceived')}
                    </Typography>
                  </Box>

                  <Chip
                    label={t(
                      displayModel.ruleHitGroups.length > 0
                        ? 'monitoring.detail.ruleHitCount'
                        : 'monitoring.detail.updateCount',
                      { count: displayModel.updateCount },
                    )}
                    size="small"
                  />
                </Box>

                {displayModel.ruleLabels.length > 0 && (
                  <Typography variant="body2">
                    <strong>{t(displayModel.ruleLabels.length === 1 ? 'monitoring.detail.triggeredRule' : 'monitoring.detail.triggeredRules')}</strong>{' '}
                    {formatList(displayModel.ruleLabels)}
                  </Typography>
                )}

                {displayModel.ruleHitSections.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {displayModel.ruleHitSections.map((section) => (
                      <Box
                        key={`${diff.id}-${section.sectionKey}`}
                        sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {section.displayName}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                          {section.entities.map((entity) => (
                            <Box
                              key={`${diff.id}-${section.sectionKey}-${entity.entityKey}`}
                              sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}
                            >
                              {!(section.sectionKey === 'this-company' && entity.entityLabel === t('monitoring.thisCompany')) && (
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {entity.entityLabel}
                                </Typography>
                              )}
                              <Box
                                component="ul"
                                sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}
                              >
                                {entity.ruleHits.map((ruleHit, index) => (
                                  <Box
                                    key={`${diff.id}-${section.sectionKey}-${entity.entityKey}-${ruleHit.ruleKey}-${index}`}
                                    component="li"
                                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}
                                  >
                                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.6 }}>
                                      {ruleHit.displayName}
                                    </Typography>
                                    {ruleHit.contextLine && (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ lineHeight: 1.6 }}
                                      >
                                        {ruleHit.contextLine}
                                      </Typography>
                                    )}
                                    {ruleHit.detailLines.map((detailLine) => (
                                      <Typography
                                        key={`${diff.id}-${section.sectionKey}-${entity.entityKey}-${ruleHit.ruleKey}-${index}-${detailLine}`}
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ lineHeight: 1.6, pl: ruleHit.contextLine ? 2 : 0 }}
                                      >
                                        {detailLine}
                                      </Typography>
                                    ))}
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ))}

                    {displayModel.showGenericChangeSection && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          {t('monitoring.detail.allChanges')}
                        </Typography>
                        <Box
                          component="ul"
                          sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}
                        >
                          {displayModel.genericSummaryItems.map((summary, index) => (
                            <Typography
                              key={`${diff.id}-generic-${index}`}
                              component="li"
                              variant="body2"
                              sx={{ lineHeight: 1.6 }}
                            >
                              {summary}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                ) : displayModel.genericSummaryItems.length > 0 ? (
                  <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {displayModel.genericSummaryItems.map((summary, index) => (
                      <Typography
                        key={`${diff.id}-${index}`}
                        component="li"
                        variant="body2"
                        sx={{ lineHeight: 1.6 }}
                      >
                        {summary}
                      </Typography>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {diff.changeSummary || t('monitoring.notificationRecorded')}
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
